/**
 * Reloads when a deploy deletes hashed chunks the open tab still references.
 * Vite emits `vite:preloadError`; Safari often surfaces the same failure as a rejection.
 */

import { getIsOnline } from '@/lib/onlineStatus';

const RELOAD_GUARD_KEY = 'tabstr.staleChunkReloadedAt';
const RELOAD_COOLDOWN_MS = 10 * 60 * 1000;
const SW_UPDATE_TIMEOUT_MS = 3_000;

const STALE_CHUNK_MESSAGES = [
  'failed to fetch dynamically imported module',
  'error loading dynamically imported module',
  'importing a module script failed',
  'is not a valid javascript mime type',
  'expected a javascript module script',
  'unable to preload css',
];

let reloadInFlight = false;

function getErrorMessage(reason: unknown): string {
  if (typeof reason === 'string') return reason;
  if (reason instanceof Error) return reason.message;
  // `unhandledrejection` reasons are often not real Errors (ErrorEvent, plain object).
  if (reason && typeof reason === 'object' && 'message' in reason) {
    return String((reason as { message: unknown }).message ?? '');
  }
  return '';
}

export function isStaleChunkError(reason: unknown): boolean {
  const message = getErrorMessage(reason).toLowerCase();
  return STALE_CHUNK_MESSAGES.some((pattern) => message.includes(pattern));
}

export function isReloadInFlight(): boolean {
  return reloadInFlight;
}

/**
 * Sync on purpose: `preventDefault()` has to run during event dispatch, so the
 * async `isDeviceOffline()` cannot be used here. `navigator.onLine === false` is
 * only reported when the browser is certain, and `onlineManager` carries the
 * Capacitor network state once `initOnlineManager` has run.
 */
function isOffline(): boolean {
  if (typeof navigator !== 'undefined' && navigator.onLine === false) return true;
  return !getIsOnline();
}

function claimReloadAllowance(): boolean {
  if (reloadInFlight) return false;
  // Offline, a chunk can fail for network reasons and a reload cannot fetch a
  // newer shell; leave the caller to show the recovery screen instead.
  if (isOffline()) return false;

  try {
    const previous = Number(window.sessionStorage.getItem(RELOAD_GUARD_KEY));
    if (previous && Date.now() - previous < RELOAD_COOLDOWN_MS) return false;
    window.sessionStorage.setItem(RELOAD_GUARD_KEY, String(Date.now()));
  } catch {
    // Safari private mode: in-memory flag still blocks a loop on this page load.
  }

  reloadInFlight = true;
  return true;
}

async function refreshServiceWorker(): Promise<void> {
  if (!('serviceWorker' in navigator)) return;
  try {
    const registration = await navigator.serviceWorker.getRegistration();
    if (!registration) return;
    await Promise.race([
      registration.update(),
      new Promise((resolve) => window.setTimeout(resolve, SW_UPDATE_TIMEOUT_MS)),
    ]);
  } catch {
    // Do not block the reload if the SW is unreachable.
  }
}

async function performReload(): Promise<void> {
  await refreshServiceWorker();
  window.location.reload();
}

export async function reloadForStaleChunk(): Promise<boolean> {
  if (!claimReloadAllowance()) return false;
  await performReload();
  return true;
}

export function forceReload(): void {
  reloadInFlight = true;
  try {
    window.sessionStorage.removeItem(RELOAD_GUARD_KEY);
  } catch {
    // Ignore unavailable storage.
  }
  window.location.reload();
}

export function setupStaleChunkReload(): () => void {
  const handlePreloadError = (event: VitePreloadErrorEvent) => {
    if (!claimReloadAllowance()) return;
    event.preventDefault();
    void performReload();
  };

  const handleRejection = (event: PromiseRejectionEvent) => {
    if (!isStaleChunkError(event.reason)) return;
    if (!claimReloadAllowance()) return;
    event.preventDefault();
    void performReload();
  };

  window.addEventListener('vite:preloadError', handlePreloadError);
  window.addEventListener('unhandledrejection', handleRejection);

  return () => {
    window.removeEventListener('vite:preloadError', handlePreloadError);
    window.removeEventListener('unhandledrejection', handleRejection);
  };
}
